import { computed, watch } from 'vue';
import { SimulationClient } from './generated/Simulation';
import user from './user';
import { fetch } from './utils/http';
import { useAsync } from './utils/useAsync';

// The fallback to process.env is for CommonJS (Storybook)
const env = import.meta.env ?? process.env;

const simulationClient = new SimulationClient(env.VUE_APP_API_BASE_URL, {
  fetch,
});

/**
 * AUDIO THROTTLING PREVENTION
 *
 * This module uses the Web Audio API to prevent browser throttling of setInterval/setTimeout.
 * Due to browser autoplay policies, audio cannot start automatically and must be enabled
 * after a user gesture (click, keypress, etc.).
 *
 * Usage:
 * 1. Call startHeartbeat() to begin the session and initialize audio context
 * 2. Call enableAudioThrottlingPrevention() on any user interaction to actually start audio
 * 3. Audio will show a volume icon on the browser tab (unavoidable)
 *
 * This prevents throttling from 4ms → 1s → 1min as the page becomes inactive.
 */

const sessionId = computed(() => user.state.sessionId);
let timer: number;
let audioContext: AudioContext | null = null;
let oscillator: OscillatorNode | null = null;
let gainNode: GainNode | null = null;
let audioStarted = false;

/**
 * Creates an AudioContext with a silent oscillator to prevent browser throttling
 * of setInterval/setTimeout. This keeps the page "active" from the browser's perspective.
 * Note: This will show a volume icon on the browser tab.
 */
function createAntiThrottleAudio(): void {
  if (audioContext) return; // Already initialized

  try {
    audioContext = new AudioContext();
    oscillator = audioContext.createOscillator();
    gainNode = audioContext.createGain();

    // Set a very low volume (nearly silent, but not muted)
    // Volume must be > 0 to prevent throttling
    gainNode.gain.value = 0.01;

    // Connect oscillator -> gain -> destination
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Set frequency (doesn't matter much at this volume)
    oscillator.frequency.value = 20; // Very low frequency

    // Don't start the oscillator yet - wait for user gesture
  } catch (error) {
    console.warn('Failed to create anti-throttle audio context:', error);
  }
}

/**
 * Starts the audio oscillator after ensuring AudioContext is resumed.
 * This must be called after a user gesture due to browser autoplay policies.
 */
async function startAntiThrottleAudio(): Promise<void> {
  if (!audioContext || audioStarted) return;

  try {
    // Resume AudioContext if it's suspended (required by browser autoplay policy)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    // Start the oscillator if it exists and isn't already started
    if (oscillator && !audioStarted) {
      oscillator.start();
      audioStarted = true;
    }
  } catch (error) {
    console.warn('Failed to start anti-throttle audio:', error);
  }
}

/**
 * Stops and cleans up the anti-throttle audio context
 */
function stopAntiThrottleAudio(): void {
  if (oscillator && audioStarted) {
    try {
      oscillator.stop();
      oscillator.disconnect();
    } catch (error) {
      // Oscillator may already be stopped
    }
    oscillator = null;
  }

  if (gainNode) {
    gainNode.disconnect();
    gainNode = null;
  }

  if (audioContext) {
    audioContext.close();
    audioContext = null;
  }

  audioStarted = false;
}

export function startHeartbeat(): void {
  // Initialize anti-throttle audio to prevent browser from throttling setInterval
  createAntiThrottleAudio();

  timer = window.setInterval(() => {
    if (sessionId.value !== undefined) {
      const { error } = useAsync(() => simulationClient.session_Heartbeat(sessionId.value as string));

      watch(error, (val) => {
        if (val !== undefined) {
          user.resetSession();
          user.setUserError(error?.value?.detail);
          clearInterval(timer);
          stopAntiThrottleAudio();
        }
      });
    }
  }, 15000);
}

/**
 * Call this function on any user gesture (click, keypress, etc.) to enable audio
 * and prevent browser throttling. This is required due to browser autoplay policies.
 */
export async function enableAudioThrottlingPrevention(): Promise<void> {
  await startAntiThrottleAudio();
}

export function markAsComplete(): void {
  if (sessionId.value !== undefined) {
    clearInterval(timer);
    stopAntiThrottleAudio();
    simulationClient.session_Completed(sessionId.value);
    user.resetSession();
  }
}
